import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Feather, Flame, MapPin, Calendar, Share2, ArrowLeft, BookHeart, Send } from "lucide-react";
import { toast } from "sonner";
import SiteLayout from "@/components/SiteLayout";
import StarField from "@/components/StarField";
import { memorialsApi, formatApiError } from "@/lib/api";
import { hexFor } from "@/lib/starColors";

export default function MemorialDetail() {
    const { slug } = useParams();
    const [m, setM] = useState(null);
    const [src, setSrc] = useState(null);
    const [loading, setLoading] = useState(true);
    const [feathers, setFeathers] = useState([]); // floating feather effect
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let alive = true;
        Promise.all([memorialsApi.get(slug), memorialsApi.image(slug).catch(() => null)])
            .then(([d, img]) => {
                if (!alive) return;
                setM(d);
                if (img?.data) setSrc(img.data);
            })
            .catch(() => {})
            .finally(() => alive && setLoading(false));
        return () => {
            alive = false;
        };
    }, [slug]);

    async function lightCandle() {
        if (busy) return;
        setBusy(true);
        try {
            const r = await memorialsApi.lightCandle(slug);
            setM((prev) => ({ ...prev, candles: r.candles }));
            toast.success("Eine Kerze leuchtet jetzt für " + m.name);
        } catch {
            toast.error("Konnte Kerze nicht anzünden");
        } finally {
            setBusy(false);
        }
    }

    async function dropFeather() {
        if (busy) return;
        setBusy(true);
        try {
            const r = await memorialsApi.dropFeather(slug);
            setM((prev) => ({ ...prev, feathers: r.feathers }));
            const id = Date.now();
            setFeathers((prev) => [...prev, id]);
            setTimeout(() => setFeathers((prev) => prev.filter((x) => x !== id)), 4000);
        } catch {
            toast.error("Konnte Feder nicht hinterlassen");
        } finally {
            setBusy(false);
        }
    }

    async function share() {
        const url = window.location.href;
        if (navigator.share) {
            try {
                await navigator.share({ title: `Taubenhimmel — ${m.name}`, url });
            } catch {
                /* user cancelled */
            }
        } else {
            await navigator.clipboard.writeText(url);
            toast.success("Link kopiert");
        }
    }

    if (loading) {
        return (
            <SiteLayout>
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center text-[#94a3b8] label gentle-pulse">
                    Wird geladen…
                </div>
            </SiteLayout>
        );
    }
    if (!m) {
        return (
            <SiteLayout>
                <div className="max-w-3xl mx-auto px-4 py-32 text-center">
                    <h1 className="font-display text-4xl mb-4">Gedenkseite nicht gefunden</h1>
                    <Link to="/taubenhimmel" className="btn-secondary mt-6 inline-flex">
                        <ArrowLeft size={14} /> Zurück zum Himmel
                    </Link>
                </div>
            </SiteLayout>
        );
    }

    return (
        <SiteLayout>
            <section className="relative">
                <StarField density={60} className="absolute inset-0" />
                <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
                    <Link
                        to="/taubenhimmel"
                        className="inline-flex items-center gap-2 text-[#94a3b8] hover:text-[#f0c674] text-sm mb-12"
                        data-testid="memorial-back"
                    >
                        <ArrowLeft size={14} /> Zurück zum Himmel
                    </Link>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                        <motion.div
                            className="lg:col-span-5"
                            initial={{ opacity: 0, scale: 0.96 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.9, ease: "easeOut" }}
                        >
                            {src ? (
                                <div className="relative">
                                    <img
                                        src={src}
                                        alt={m.name}
                                        className="w-full aspect-[4/5] object-cover rounded-2xl shadow-2xl fade-edges"
                                        data-testid="memorial-image"
                                    />
                                    <div
                                        className="absolute -inset-2 rounded-3xl pointer-events-none"
                                        style={{
                                            background: `radial-gradient(ellipse at center, ${hexFor(m.star_color) || "#f0c674"}33 0%, transparent 70%)`,
                                        }}
                                    />
                                </div>
                            ) : (
                                <div className="w-full aspect-[4/5] glass flex items-center justify-center">
                                    <Feather size={48} className="text-[#f0c674]/40" />
                                </div>
                            )}
                        </motion.div>

                        <motion.div
                            className="lg:col-span-7 relative"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, ease: "easeOut", delay: 0.2 }}
                        >
                            <div className="label mb-4">In Erinnerung an</div>
                            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-none mb-6" data-testid="memorial-name">
                                {m.name}
                            </h1>
                            <div className="flex flex-wrap items-center gap-4 text-sm text-[#94a3b8] mb-8">
                                {m.death_date && (
                                    <span className="inline-flex items-center gap-1.5">
                                        <Calendar size={14} /> {formatDate(m.death_date)}
                                    </span>
                                )}
                                {m.location && (
                                    <span className="inline-flex items-center gap-1.5">
                                        <MapPin size={14} /> {m.location}
                                    </span>
                                )}
                            </div>

                            {m.story && (
                                <p className="font-display text-xl sm:text-2xl leading-relaxed text-[#f8fafc]/90 italic mb-8" data-testid="memorial-story">
                                    „{m.story}"
                                </p>
                            )}

                            {m.message && (
                                <div className="glass p-6 mb-8">
                                    <div className="label mb-2">Eine Nachricht</div>
                                    <p className="text-[#94a3b8] leading-relaxed">{m.message}</p>
                                    {m.owner_name && (
                                        <div className="mt-3 text-sm text-[#64748b]">— {m.owner_name}</div>
                                    )}
                                </div>
                            )}

                            <div className="flex flex-wrap items-center gap-3 mb-10">
                                <button
                                    onClick={lightCandle}
                                    className="btn-primary"
                                    disabled={busy}
                                    data-testid="memorial-candle-button"
                                >
                                    <Flame size={16} /> Kerze anzünden
                                </button>
                                <button
                                    onClick={dropFeather}
                                    className="btn-secondary"
                                    disabled={busy}
                                    data-testid="memorial-feather-button"
                                >
                                    <Feather size={16} /> Feder hinterlassen
                                </button>
                                <button onClick={share} className="btn-ghost" data-testid="memorial-share-button">
                                    <Share2 size={14} /> Teilen
                                </button>
                            </div>

                            <div className="flex items-center gap-8 text-sm">
                                <Counter icon={Flame} label="Kerzen" value={m.candles ?? 0} color="#ff9d00" />
                                <Counter icon={Feather} label="Federn" value={m.feathers ?? 0} color="#f0c674" />
                            </div>

                            {/* Floating feathers overlay */}
                            <div className="absolute right-4 bottom-0 pointer-events-none">
                                <AnimatePresence>
                                    {feathers.map((id) => (
                                        <motion.span
                                            key={id}
                                            className="absolute right-0 bottom-0 feather-float text-[#f0c674]"
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                        >
                                            <Feather size={24} strokeWidth={1.2} />
                                        </motion.span>
                                    ))}
                                </AnimatePresence>
                            </div>
                        </motion.div>
                    </div>

                    <Guestbook slug={slug} memorialName={m.name} />
                </div>
            </section>
        </SiteLayout>
    );
}

function Guestbook({ slug, memorialName }) {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [name, setName] = useState("");
    const [message, setMessage] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        let alive = true;
        memorialsApi
            .listGuestbook(slug)
            .then((d) => alive && setEntries(d.entries || []))
            .catch(() => {})
            .finally(() => alive && setLoading(false));
        return () => {
            alive = false;
        };
    }, [slug]);

    async function submit(e) {
        e.preventDefault();
        const msg = message.trim();
        if (!msg) {
            toast.error("Bitte schreibe eine kurze Nachricht");
            return;
        }
        if (msg.length > 200) {
            toast.error("Maximal 200 Zeichen");
            return;
        }
        setSubmitting(true);
        try {
            const r = await memorialsApi.addGuestbookEntry(slug, {
                name: name.trim() || null,
                message: msg,
            });
            setEntries((prev) => [r.entry, ...prev]);
            setMessage("");
            toast.success("Dein Eintrag ist im Gästebuch");
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    }

    const remaining = 200 - message.length;

    return (
        <motion.div
            className="mt-16 sm:mt-24 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            data-testid="guestbook-section"
        >
            <div className="flex items-center gap-3 mb-2">
                <BookHeart size={18} className="text-[#f0c674]" strokeWidth={1.5} />
                <div className="label">Gästebuch</div>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl mb-3">
                Ein paar Worte für {memorialName}
            </h2>
            <p className="text-sm text-[#94a3b8] mb-8 max-w-xl">
                Schreibe einen kurzen Gruß, eine Erinnerung oder ein leises Wort.
                Anonym oder mit deinem Vornamen — bis zu 200 Zeichen.
            </p>

            <form onSubmit={submit} className="glass p-5 sm:p-6 mb-10 space-y-4" data-testid="guestbook-form">
                <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value.slice(0, 40))}
                    placeholder="Dein Vorname (optional)"
                    className="input-field"
                    maxLength={40}
                    data-testid="guestbook-name-input"
                />
                <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value.slice(0, 200))}
                    placeholder="Deine Worte für diese Taube…"
                    className="input-field min-h-[100px] resize-none"
                    maxLength={200}
                    required
                    data-testid="guestbook-message-input"
                />
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <span className={`text-xs font-mono ${remaining < 20 ? "text-[#f0c674]" : "text-[#64748b]"}`}>
                        {remaining} Zeichen übrig
                    </span>
                    <button
                        type="submit"
                        className="btn-primary"
                        disabled={submitting || !message.trim()}
                        data-testid="guestbook-submit"
                    >
                        <Send size={14} />
                        {submitting ? "Wird gesendet…" : "Im Gästebuch verewigen"}
                    </button>
                </div>
            </form>

            {loading ? (
                <div className="text-[#94a3b8] label gentle-pulse text-center py-8">
                    Wird geladen…
                </div>
            ) : entries.length === 0 ? (
                <div className="text-center text-[#64748b] text-sm italic py-8" data-testid="guestbook-empty">
                    Noch keine Einträge — sei die erste leise Stimme.
                </div>
            ) : (
                <div className="space-y-4" data-testid="guestbook-entries">
                    <AnimatePresence initial={false}>
                        {entries.map((e) => (
                            <motion.div
                                key={e.id}
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.5 }}
                                className="glass p-4 sm:p-5"
                                data-testid={`guestbook-entry-${e.id}`}
                            >
                                <p className="text-[#f8fafc]/90 leading-relaxed whitespace-pre-wrap text-sm sm:text-base">
                                    {e.message}
                                </p>
                                <div className="flex items-center gap-2 mt-3 text-xs text-[#64748b] font-mono uppercase tracking-widest">
                                    <span>— {e.name || "Anonym"}</span>
                                    <span>·</span>
                                    <span>{formatRelative(e.created_at)}</span>
                                </div>
                            </motion.div>
                        ))}
                    </AnimatePresence>
                </div>
            )}
        </motion.div>
    );
}

function formatRelative(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const diff = (Date.now() - d.getTime()) / 1000;
        if (diff < 60) return "gerade eben";
        if (diff < 3600) return `vor ${Math.floor(diff / 60)} Min`;
        if (diff < 86400) return `vor ${Math.floor(diff / 3600)} Std`;
        if (diff < 86400 * 7) return `vor ${Math.floor(diff / 86400)} Tg`;
        return d.toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" });
    } catch {
        return iso;
    }
}

function Counter({ icon: Icon, label, value, color }) {
    return (
        <div className="flex items-center gap-3">
            <span className="flex items-center justify-center w-10 h-10 rounded-full bg-white/5 border border-white/10">
                <Icon size={16} style={{ color }} />
            </span>
            <div>
                <div className="font-display text-2xl leading-none">{value}</div>
                <div className="text-xs text-[#64748b] uppercase tracking-widest mt-1">{label}</div>
            </div>
        </div>
    );
}

function formatDate(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        if (isNaN(d.getTime())) return iso;
        return d.toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" });
    } catch {
        return iso;
    }
}
